import Utils from '../../util.js';

class Chip extends HTMLElement {

    #template = document.createElement('template');
    $templateMarkup = `
        <button type="button">
        </button>
    `;

    static CHIP_TYPES = {
        INPUT: 'input',
        FILTER: 'filter'
    };

    constructor() {
        super();
        this.attachShadow({mode: 'open'});
        this.addStyle = Utils.addStyle;
        this.addStyle('/src/styles/neptune.css');
        this.addStyle('/src/components/atoms/chip/chip.css');
        this.#template.innerHTML = this.$templateMarkup;
        this.shadowRoot.appendChild(this.#template.content.cloneNode(true));
        this.addValueToAttribute = Utils.addValueToAttribute;
        this.removeValueFromAttribute = Utils.removeValueFromAttribute;
        this.failed = false;
    }

    connectedCallback() {
        if(!this.failed){
            this.setChip();
        }
        this.shadowRoot.querySelector('button').addEventListener('click', (e) => {
            if(this.hasAttribute('disabled')) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }
            
            const type = this.getAttribute('type');
            const isDismissible = this.getAttribute('dismissible') === 'true';
            const isSelected = this.getAttribute('selected') === 'true';
            
            if(type === 'input' && isDismissible) {
                this.remove();
                return;
            }
            
            this.setAttribute('selected', String(!isSelected));
            
            if(type === 'filter' && this.getAttribute('show-dropdown') === 'true') {
                // open the dropdown menu - not handled in this component
            }
        });
    }

    static get observedAttributes() {
        return ['type', 'selected', 'disabled', 'dismissible', 'left-icon', 'show-dropdown', 'counter'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if(this.failed) return;
        if(oldValue === newValue) return;
        if(name === 'disabled') {
            const chipElement = this.shadowRoot.querySelector('button');
            chipElement.disabled = this.hasAttribute('disabled');
            if(chipElement.disabled) {
                chipElement.setAttribute('aria-disabled', 'true');
            } else {
                chipElement.removeAttribute('aria-disabled');
            }
            return;
        }
        if(oldValue !== newValue && this.isConnected) {
            this.setChip();
        }
    }

    setChip() {
        const chipAttributes = this.getChipAttributes();
        chipAttributes.type = this.#validateChipType(chipAttributes.type);

        const chipElement = this.shadowRoot.querySelector('button');

        chipElement.innerHTML = '';

        this.#insertElementsIntoChip(chipAttributes, chipElement);
        this.updateChipSelectionState(chipAttributes, chipElement);
        this.updateChipAttributes(chipAttributes);
    }

    updateChipSelectionState(chipAttributes, chipElement) {
        if (chipAttributes.isSelected) {
            chipElement.setAttribute('selected', '');
        } else {
            chipElement.removeAttribute('selected');
        }

        if (chipAttributes.isDisabled) {
            chipElement.setAttribute('disabled', '');
            chipElement.setAttribute('tabindex', '-1');
        } else {
            chipElement.removeAttribute('disabled');
            chipElement.setAttribute('tabindex', '0');
        }
    }

    getChipAttributes() {
        return {
            type: (this.getAttribute('type') || '').trim().toLowerCase(),
            isSelected: this.getAttribute('selected') === 'true' ? true : false,
            isDismissible: this.getAttribute('dismissible') === 'true' ? true : false,
            showDropdown: this.getAttribute('show-dropdown') === 'true' ? true : false,
            counter: (this.getAttribute('counter') || '').trim(),
            leftIcon: (this.getAttribute('left-icon') || '').trim(),
            content: this.textContent.trim()
        };
    }

    #validateChipType(type) {
        if(type !== Chip.CHIP_TYPES.INPUT && type !== Chip.CHIP_TYPES.FILTER) {
            this.failed = true;
            throw new Error(`Invalid type value "${type}" for <neptune-chip>. The valid types are: ${Object.values(Chip.CHIP_TYPES).join(', ')}.`);
        }
        return type;
    }

    #insertElementsIntoChip(chipAttributes, chipElement) {
        const fragment = new DocumentFragment();
        if(chipAttributes.leftIcon) {
            const leftIconElement = this.#createIcon(chipAttributes.leftIcon);
            fragment.appendChild(leftIconElement);
        }
        if(chipAttributes.content) {
            const contentElement = this.#createText(chipAttributes.content);
            fragment.appendChild(contentElement);
        }
        if(chipAttributes.type === Chip.CHIP_TYPES.INPUT) {
            if(chipAttributes.isDismissible){
                const dismissIconElement = this.#createIcon('close');
                fragment.appendChild(dismissIconElement);
            }
        } else if(chipAttributes.type === Chip.CHIP_TYPES.FILTER) {
            if(chipAttributes.showDropdown){
                const dropdownIconElement = this.#createIcon('arrow_drop_down');
                fragment.appendChild(dropdownIconElement);
            } else if(chipAttributes.counter) {
                const counterElement = this.#createCounter(chipAttributes.counter);
                fragment.appendChild(counterElement);
            }
        }
        chipElement.appendChild(fragment);
    }

    #createIcon(iconName) {
        const iconElement = document.createElement('i');
        iconElement.className = 'material-symbols-rounded';
        iconElement.textContent = iconName;
        return iconElement;
    }

    #createText(text) {
        const textElement = document.createElement('span');
        textElement.textContent = text;
        return textElement;
    }

    #createCounter(count) {
        const counterElement = document.createElement('span');
        counterElement.className = 'counter';
        counterElement.textContent = count;
        return counterElement;
    }

    updateChipAttributes(chipAttributes) {
        if (this.getAttribute('type') !== chipAttributes.type) {
            this.setAttribute('type', chipAttributes.type);
        }
        if (this.getAttribute('selected') !== String(chipAttributes.isSelected)) {
            this.setAttribute('selected', chipAttributes.isSelected);
        }
        if (this.getAttribute('dismissible') !== String(chipAttributes.isDismissible)) {
            this.setAttribute('dismissible', chipAttributes.isDismissible);
        }
        if (chipAttributes.showDropdown && !this.hasAttribute('show-dropdown')) {
            this.setAttribute('show-dropdown', chipAttributes.showDropdown);
        } else if (!chipAttributes.showDropdown && this.hasAttribute('show-dropdown')) {
            this.removeAttribute('show-dropdown');
        }
        if (chipAttributes.counter && this.getAttribute('counter') !== chipAttributes.counter) {
            this.setAttribute('counter', chipAttributes.counter);
        } else if (!chipAttributes.counter && this.hasAttribute('counter')) {
            this.removeAttribute('counter');
        }
    }
}

customElements.define('neptune-chip', Chip);

export default Chip;